/**
 * The step-up (re-authentication) challenge, at the transport level.
 *
 * `web-merchant-portal-bff` answers a capability classed `SECRET` / `FINANCIAL` / `GOVERNANCE` with
 * **HTTP 403 + `code: "STEP_UP_REQUIRED"`** when the caller *holds* the permission but has not proved who
 * they are recently enough to use it (adr-auth-004, plan-auth-001 Phase 4.3). That is a **challenge, not a
 * refusal** — the only correct reaction is to re-authenticate at the ACR the error names and carry on, not
 * to hide the affordance.
 *
 * **Why 403 and not 401.** `apiClient`'s response interceptor treats every 401 as "the BFF session died"
 * and answers it with a transparent Keycloak round-trip. If the step-up challenge arrived as 401 that
 * round-trip would ask for the *same* assurance level, come back no fresher than before, and challenge
 * again — a reload loop that can never terminate. The BFF therefore returns 403 and the SPA switches on
 * the `code`, exactly as it already does for `TENANT_ACCESS_DENIED`.
 *
 * This module owns the pieces that must survive a **full-page navigation**: the challenge shape, and the
 * record of what the user was doing when it arrived. `apiClient` writes that record and navigates;
 * `StepUpReplay` (mounted in `AppLayout`) reads it back on the way in and finishes the job.
 *
 * ## What can and cannot be replayed
 *
 * Only a body axios has already reduced to a **string** (i.e. anything JSON) or **no body at all** is kept.
 * A `FormData` body — the media-upload path — is deliberately marked non-replayable: a `File` cannot be put
 * in `sessionStorage`, so any "replay" of it would silently send an empty or corrupted upload. For that
 * shape the user is brought back to the screen and told to repeat the action, which loses a file selection
 * but never fabricates one.
 *
 * Request headers are **not** persisted. The replay goes back through `apiClient`, which re-attaches the
 * CSRF token and the JSON content type; persisting arbitrary headers would mean writing whatever a call
 * site happened to attach into browser storage for no benefit.
 */

import type { InternalAxiosRequestConfig } from 'axios';

/** The stable machine-readable code the BFF returns with its 403 challenge. */
export const STEP_UP_REQUIRED_CODE = 'STEP_UP_REQUIRED';

/** Where the interrupted action is parked across the Keycloak round-trip. */
export const STEP_UP_PENDING_KEY = 'obzr:auth:step_up_pending';

/** When the last step-up navigation started — the loop-breaker, mirroring `REAUTH_ATTEMPT_KEY`. */
export const STEP_UP_ATTEMPT_KEY = 'obzr:auth:step_up_at';

/**
 * How long a just-started step-up suppresses another one.
 *
 * The failure this bounds is real and, until the BFF forwards `acr_values` to Keycloak, likely: the
 * round-trip completes, the `acr` claim is unchanged, the replay is challenged again. Without the window
 * that is an infinite redirect loop. With it, the second challenge inside the window rejects normally so
 * the UI can explain itself. Long enough to cover a login that requires typing a password and a TOTP code.
 */
export const STEP_UP_COOLDOWN_MS = 90_000;

/**
 * How long a parked action stays replayable.
 *
 * Generous enough for a login that needs a first-time authenticator enrolment, short enough that a tab
 * resumed from sleep tomorrow does not suddenly fire a change its owner has forgotten about.
 */
export const STEP_UP_PENDING_TTL_MS = 15 * 60_000;

/** What the BFF's `details` carry on a `STEP_UP_REQUIRED` response. */
export interface StepUpChallenge {
  /** The permission code whose capability demands elevated assurance. */
  permission: string;
  /** The ACR / level-of-assurance value to ask Keycloak for (e.g. `gold`). */
  requiredAcr: string;
  /** How recent the authentication must be, in seconds. `null` when the BFF did not say. */
  maxAgeSeconds: number | null;
}

/** The interrupted action, in a form that survives `sessionStorage` and a full page load. */
export interface PendingStepUpAction {
  /** Uppercased HTTP method. */
  method: string;
  /** Request path, as the call site wrote it (relative to `baseURL`). */
  url: string;
  baseURL?: string;
  /** Query parameters, if any. Only plain JSON survives here. */
  params?: unknown;
  /** The request body verbatim — axios has already reduced a JSON body to a string by this point. */
  data?: string;
  /**
   * False when the body could not be preserved (a `FormData` upload, a `Blob`). The action is still
   * parked so the user is returned to the right screen and told what happened; it is simply never re-sent.
   */
  replayable: boolean;
  /** SPA path to come back to, or `null` when there was nothing worth restoring. */
  returnPath: string | null;
  permission: string;
  requiredAcr: string;
  /** Epoch ms — see {@link STEP_UP_PENDING_TTL_MS}. */
  startedAt: number;
}

/**
 * Reads a `STEP_UP_REQUIRED` challenge out of a response body, or `null` if that is not what this is.
 *
 * `details` values arrive as strings (the BFF builds a `Map<String, String>`), so `maxAgeSeconds` is
 * parsed rather than trusted. A body carrying the code but no usable `requiredAcr` is **not** treated as a
 * challenge: navigating to Keycloak without knowing what to ask for would burn a round-trip to arrive at
 * exactly the assurance level that was just rejected.
 */
export function parseStepUpChallenge(data: unknown): StepUpChallenge | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const body = data as { code?: unknown; details?: unknown };
  if (body.code !== STEP_UP_REQUIRED_CODE) {
    return null;
  }
  const details = (typeof body.details === 'object' && body.details !== null ? body.details : {}) as {
    permission?: unknown;
    requiredAcr?: unknown;
    maxAgeSeconds?: unknown;
  };
  const requiredAcr = typeof details.requiredAcr === 'string' ? details.requiredAcr.trim() : '';
  if (requiredAcr === '') {
    return null;
  }
  const maxAge = Number(details.maxAgeSeconds);
  return {
    permission: typeof details.permission === 'string' ? details.permission : '',
    requiredAcr,
    maxAgeSeconds: Number.isFinite(maxAge) && maxAge > 0 ? maxAge : null,
  };
}

/** The SPA path the user is on, in the same shape `safeReturnPath` stores. */
export function currentSpaPath(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

/** Everything JSON survives; a `FormData`/`Blob` body does not — see the module doc. */
function captureBody(data: unknown): { replayable: boolean; body?: string } {
  if (data === undefined || data === null) {
    return { replayable: true };
  }
  if (typeof data === 'string') {
    return { replayable: true, body: data };
  }
  return { replayable: false };
}

/** Query params are kept only when they round-trip through JSON unchanged. */
function captureParams(params: unknown): { ok: boolean; value?: unknown } {
  if (params === undefined || params === null) {
    return { ok: true };
  }
  try {
    return { ok: true, value: JSON.parse(JSON.stringify(params)) as unknown };
  } catch {
    return { ok: false };
  }
}

/** Builds the parked record from the request that was challenged. */
export function describePendingStepUp(
  config: InternalAxiosRequestConfig,
  challenge: StepUpChallenge,
  returnPath: string | null,
): PendingStepUpAction {
  const body = captureBody(config.data);
  const params = captureParams(config.params);
  return {
    method: (config.method ?? 'get').toUpperCase(),
    url: config.url ?? '',
    baseURL: config.baseURL,
    params: params.value,
    data: body.body,
    replayable: body.replayable && params.ok,
    returnPath,
    permission: challenge.permission,
    requiredAcr: challenge.requiredAcr,
    startedAt: Date.now(),
  };
}

/**
 * Best-effort `sessionStorage` access.
 *
 * Every read and write is guarded because `sessionStorage` throws outright when a browser blocks site data
 * (privacy modes, enterprise policy). Failing open costs the replay; throwing would escape the response
 * interceptor and leave the user unable to re-authenticate at all — the same trade `apiClient` already
 * makes for the re-auth return path.
 */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the replay degrades to "you are back on your screen, please repeat the action".
  }
}

function removeSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing was stored either, so there is nothing to clear.
  }
}

export function writePendingStepUp(action: PendingStepUpAction): void {
  try {
    writeSession(STEP_UP_PENDING_KEY, JSON.stringify(action));
  } catch {
    // A body we could not stringify would already have been marked non-replayable; belt and braces.
  }
}

export function clearPendingStepUp(): void {
  removeSession(STEP_UP_PENDING_KEY);
}

/** The parked action, or `null` when there is none or it is unreadable. */
export function readPendingStepUp(): PendingStepUpAction | null {
  const raw = readSession(STEP_UP_PENDING_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PendingStepUpAction;
    if (typeof parsed?.method !== 'string' || typeof parsed?.url !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Consume-once accessor: returns the parked action **and removes it** — but only once the browser is back
 * on the screen the action came from.
 *
 * The path check is what makes the replay land in the right place. Keycloak returns to the SPA root
 * (`defaultSuccessUrl`), and `BffSessionProvider` only then restores the saved path with a second,
 * full-page `location.replace`. Replaying on the root load would fire the request into a document that is
 * about to be torn down, aborting it mid-flight. Waiting for the path to match means the replay happens on
 * the document the user actually ends up looking at.
 *
 * Removal happens **before** the request goes out, on purpose: a replay that fails must not be retried
 * automatically on the next mount.
 */
export function takePendingStepUp(currentPath: string): PendingStepUpAction | null {
  const pending = readPendingStepUp();
  if (!pending) {
    return null;
  }
  if (!Number.isFinite(pending.startedAt) || Date.now() - pending.startedAt > STEP_UP_PENDING_TTL_MS) {
    clearPendingStepUp();
    return null;
  }
  if (pending.returnPath && pending.returnPath !== currentPath) {
    return null;
  }
  clearPendingStepUp();
  return pending;
}

/** Records that a step-up navigation has just started. */
export function markStepUpStarted(): void {
  writeSession(STEP_UP_ATTEMPT_KEY, String(Date.now()));
}

/**
 * True when a step-up was started so recently that starting another would be a loop.
 *
 * Deliberately **not** cleared by a successful `GET /api/v1/me`, unlike the 401 cooldown: coming back
 * authenticated is precisely the state in which the loop would restart, so clearing it there would defeat
 * the guard. It expires on time alone, so a user who genuinely retries later gets a fresh challenge.
 */
export function stepUpOnCooldown(): boolean {
  const startedAt = Number(readSession(STEP_UP_ATTEMPT_KEY) ?? '0');
  return Number.isFinite(startedAt) && startedAt > 0 && Date.now() - startedAt < STEP_UP_COOLDOWN_MS;
}

/** Drops the loop-breaker — used by tests and after a replay that actually worked. */
export function clearStepUpCooldown(): void {
  removeSession(STEP_UP_ATTEMPT_KEY);
}

/**
 * The BFF authorize URL with the ACR the challenge named.
 *
 * Login is a full-page navigate to `{VITE_API_BASE_URL}/oauth2/authorization/keycloak` (see
 * `sessionApi.redirectToLogin`); a step-up is the same navigate carrying `acr_values`, which is what asks
 * Keycloak for a higher level of assurance instead of returning the session that was just rejected.
 */
export function stepUpAuthorizeUrl(authorizeUrl: string, requiredAcr: string): string {
  const separator = authorizeUrl.includes('?') ? '&' : '?';
  return `${authorizeUrl}${separator}acr_values=${encodeURIComponent(requiredAcr)}`;
}
